import { getForestOwner } from '@services/ForestOwners/ForestOwnerService'
import { CooperativeMembershipLookupError } from '@/errors/cooperativeMembership'
import { shouldShowCooperativeMembershipWarning } from './cooperativeMembershipWarningPolicy'
import {
  type CooperativeMembershipWarningContext,
  resolveCooperativeMembershipForProperty,
} from './resolveCooperativeMembershipForProperty'

export type CooperativeMembershipEvaluationResult =
  | { action: 'proceed' }
  | { action: 'warn'; warning: CooperativeMembershipWarningContext }
  | { action: 'error'; error: CooperativeMembershipLookupError }

export async function evaluateCooperativeMembershipWarning(input: {
  propertyId: string
  targetForestOwnerId?: string
  targetForestOwnerMembershipId?: string | null
  propertyNumberOverride?: string
}): Promise<CooperativeMembershipEvaluationResult> {
  let targetForestOwnerMembershipId = input.targetForestOwnerMembershipId

  if (targetForestOwnerMembershipId === undefined && input.targetForestOwnerId) {
    try {
      const forestOwner = await getForestOwner(input.targetForestOwnerId)
      targetForestOwnerMembershipId = forestOwner.cooperativeMembershipId ?? null
    } catch (error) {
      return {
        action: 'error',
        error: new CooperativeMembershipLookupError(
          'Failed to load target forest owner cooperative membership',
          error,
        ),
      }
    }
  }

  try {
    const warning = await resolveCooperativeMembershipForProperty(input.propertyId, {
      propertyNumberOverride: input.propertyNumberOverride,
    })

    if (!warning) {
      return { action: 'proceed' }
    }

    if (warning.isGenericWarning) {
      return { action: 'warn', warning }
    }

    const shouldWarn = shouldShowCooperativeMembershipWarning({
      resolvedMembershipId: warning.cooperativeMembershipId,
      targetForestOwnerMembershipId: targetForestOwnerMembershipId ?? null,
      membershipExists: true,
      hasDisplayContext: true,
    })

    if (!shouldWarn) {
      return { action: 'proceed' }
    }

    return { action: 'warn', warning }
  } catch (error) {
    if (error instanceof CooperativeMembershipLookupError) {
      return { action: 'error', error }
    }

    return {
      action: 'error',
      error: new CooperativeMembershipLookupError('Cooperative membership lookup failed', error),
    }
  }
}
